import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound, useSearch } from "@tanstack/react-router";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { menuQueryOptions } from "@/api/vendure/menu/menu.query.options";
import type { MenuProduct } from "@/api/vendure/menu/menu.types";
import { buildCategoryIndex } from "@/api/vendure/menu/menu.utils";
import { readMenuJsonLdOnServer } from "@/api/vendure/menu/menu-json-ld.query.options";
import { merchantQueryOptions } from "@/api/vendure/merchant/merchant.query.options";
import type { Venue } from "@/api/vendure/merchant/merchant.types";
import { isVenueLive } from "@/api/vendure/merchant/merchant.utils";
import { LoginSheet } from "@/components/business/auth/LoginSheet";
import { BusinessHeader } from "@/components/business/BusinessHeader";
import { CtaBanner } from "@/components/business/banner/CtaBanner";
import { deliveryMethodFromParam } from "@/components/business/business.utils";
import { CartBar } from "@/components/business/cart/CartBar";
import { CategoryChips } from "@/components/business/category/CategoryChips";
import { StoreNotLive } from "@/components/business/not-live/StoreNotLive";
import { OrderTypeSheet } from "@/components/business/order-type/OrderTypeSheet";
import { OrderTypeSummary } from "@/components/business/order-type/OrderTypeSummary";
import { scheduledUnixTimestamp } from "@/components/business/order-type/order.utils";
import { TableSpotSheet } from "@/components/business/order-type/TableSpotSheet";
import { ProductDetailSheet } from "@/components/business/products/ProductDetailSheet";
import { ProductGrid } from "@/components/business/products/ProductGrid";
import { TableSummary } from "@/components/business/TableSummary";
import { ScrollProgress } from "@/components/ui/scroll-progress";
import { useActivateVenue } from "@/hooks/useActivateVenue";
import { getCountryCode } from "@/lib/geocode";
import { venueManifestPath } from "@/lib/manifest";
import { getSiteOrigin } from "@/lib/origin";
import {
  absoluteUrl,
  buildHiddenVenueHead,
  buildRestaurantJsonLd,
  buildVenueDescription,
  buildVenueTitle,
  currencyOf,
  serializeJsonLd,
} from "@/lib/seo";
import type { JsonLd } from "@/lib/seo.types";
import {
  currencyCodeAtom,
  DEFAULT_DELIVERY_METHOD,
  deliveryMethodAtom,
  operationCountryAtom,
  selectedDayAtom,
  selectedTimeAtom,
  tableSpotAtom,
  tableSpotTypeAtom,
  venueIsOnlineAtom,
} from "@/stores/delivery.store";

export const Route = createFileRoute("/business/$slug")({
  loader: async ({
    params: { slug },
    context: { queryClient },
  }): Promise<{ venues: Venue[]; origin: string; menuJsonLd?: JsonLd }> => {
    const venues = await queryClient
      .ensureQueryData(merchantQueryOptions(slug))
      .catch(() => {
        throw notFound();
      });

    if (!venues || venues.length === 0 || !venues[0].token) throw notFound();

    if (!isVenueLive(venues[0])) return { venues, origin: getSiteOrigin() };

    const menuJsonLd = await readMenuJsonLdOnServer(
      venues[0].token,
      DEFAULT_DELIVERY_METHOD,
      currencyOf(venues[0]),
    ).catch(() => undefined);

    return { venues, origin: getSiteOrigin(), menuJsonLd };
  },

  head: ({ loaderData, match }) => {
    const venue = loaderData?.venues?.[0];
    if (!venue || !loaderData) return {};

    const { origin } = loaderData;
    const url = absoluteUrl(origin, `/business/${match.params.slug}`);
    const image = absoluteUrl(origin, venue.image_url ?? "/logo.png");
    const title = buildVenueTitle(venue);
    const description = buildVenueDescription(venue);
    const favicon = venue.image_url ?? "/logo.png";

    if (!isVenueLive(venue)) {
      return buildHiddenVenueHead({
        venueName: venue.name,
        title,
        url,
        image,
        favicon,
      });
    }

    const jsonLd = buildRestaurantJsonLd({
      venue,
      url,
      image,
      menuJsonLd: loaderData.menuJsonLd,
    });

    return {
      links: [
        { rel: "canonical", href: url },
        { rel: "icon", type: "image/png", href: favicon },
        { rel: "apple-touch-icon", href: favicon },
        { rel: "manifest", href: venueManifestPath(match.params.slug) },
      ],
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:type", content: "restaurant.restaurant" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: venue.name },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: serializeJsonLd(jsonLd),
        },
      ],
    };
  },

  component: Sentry.withProfiler(StorePage, { name: "RestaurantPage" }),
});

function StorePage() {
  const { venues } = Route.useLoaderData();
  const venue = venues[0];
  if (!isVenueLive(venue)) return <StoreNotLive venue={venue} />;
  return <RestaurantPage venue={venue} />;
}

function RestaurantPage({ venue }: { venue: Venue }) {
  const { slug } = Route.useParams();

  useActivateVenue();

  const setIsOnline = useSetAtom(venueIsOnlineAtom);
  const setCurrencyCode = useSetAtom(currencyCodeAtom);
  const [selectedProduct, setSelectedProduct] = useState<MenuProduct | null>(
    null,
  );
  const productGridId = useId();
  const categoryRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const setTableSpot = useSetAtom(tableSpotAtom);
  const setTableSpotType = useSetAtom(tableSpotTypeAtom);
  const setDeliveryMethod = useSetAtom(deliveryMethodAtom);
  const deliveryMethod = useAtomValue(deliveryMethodAtom);
  const selectedDay = useAtomValue(selectedDayAtom);
  const selectedTime = useAtomValue(selectedTimeAtom);
  const spot = useAtomValue(tableSpotAtom);

  const scheduledTimestamp = scheduledUnixTimestamp(selectedDay, selectedTime);
  const [operationCountry, setOperationCountry] = useAtom(operationCountryAtom);

  const search = useSearch({ from: "/business/$slug" }) as {
    spot?: string;
    supportType?: string;
    type?: string;
  };

  useEffect(() => {
    if (search.spot) return;
    const method = deliveryMethodFromParam(
      search.type,
      venue.delivery_methods ?? [],
    );
    if (method) setDeliveryMethod(method);
  }, [search.spot, search.type, venue.delivery_methods, setDeliveryMethod]);

  useEffect(() => {
    if (search.spot) {
      setTableSpot(search.spot);
      setTableSpotType(search.supportType ?? "table");
      setDeliveryMethod("dine_in");
    }
  }, [
    search.spot,
    search.supportType,
    setTableSpot,
    setTableSpotType,
    setDeliveryMethod,
  ]);

  const [hasSettledOrderType, setHasSettledOrderType] = useState(false);
  useEffect(() => setHasSettledOrderType(true), []);

  const {
    data: menuCategories,
    isPending: isMenuPending,
    isSuccess: isMenuSuccess,
  } = useQuery({
    ...menuQueryOptions(venue.token, deliveryMethod, scheduledTimestamp),
    enabled: hasSettledOrderType,
  });
  const isMenuEmpty = isMenuSuccess && (menuCategories?.length ?? 0) === 0;

  useEffect(() => {
    if (venue.currency) setCurrencyCode(venue.currency);
  }, [venue.currency, setCurrencyCode]);

  useEffect(() => {
    setIsOnline(venue.open_status?.is_open ?? false);
  }, [venue.open_status?.is_open, setIsOnline]);

  const menu = useMemo(
    () => buildCategoryIndex(menuCategories ?? []),
    [menuCategories],
  );

  useEffect(() => {
    if (operationCountry || !venue.latitude || !venue.longitude) return;
    getCountryCode(venue.latitude, venue.longitude).then((code) => {
      if (code) setOperationCountry(code);
    });
  }, [venue.latitude, venue.longitude, operationCountry, setOperationCountry]);

  return (
    <div className="relative min-h-screen">
      <ScrollProgress />
      {venue.banner?.enabled ? <CtaBanner banner={venue.banner} /> : null}
      <BusinessHeader venue={venue} />
      {deliveryMethod === "dine_in" || spot ? (
        <TableSummary />
      ) : (
        <OrderTypeSummary venueAddress={venue.address} />
      )}
      <CategoryChips
        menu={menu}
        isLoading={isMenuPending}
        venueName={venue.name}
        categoryRefsMap={categoryRefsMap}
      />
      <ProductGrid
        id={productGridId}
        menu={menu}
        isLoading={isMenuPending}
        isEmpty={isMenuEmpty}
        onProductClick={setSelectedProduct}
        registerRef={(id, el) => {
          if (el) categoryRefsMap.current.set(id, el);
          else categoryRefsMap.current.delete(id);
        }}
      />
      <CartBar />
      <ProductDetailSheet
        product={selectedProduct}
        id={productGridId}
        onClose={() => setSelectedProduct(null)}
      />
      {!spot && (
        <OrderTypeSheet
          deliveryMethods={venue.delivery_methods}
          schedule={venue.schedule}
          isOpen={venue.open_status?.is_open ?? false}
          slug={slug}
        />
      )}
      <TableSpotSheet />
      <LoginSheet />
    </div>
  );
}
